package com.macrofocus.nastac.components

import com.macrofocus.eflux.animation.Easing
import com.macrofocus.eflux.animation.Playback
import com.macrofocus.eflux.animation.timeline
import com.macrofocus.nastac.DataLoader
import com.macrofocus.nastac.theme.ThemeContext
import com.macrofocus.nastac.ui.PaletteType
import com.macrofocus.nastac.ui.react.toState
import mui.icons.material.ExpandMore
import mui.material.*
import mui.system.sx
import react.*
import web.cssom.pct
import web.dom.ElementId

external interface QueryProps : Props {
    var dataLoader: DataLoader
}

val Query = FC<QueryProps> { props ->
    var theme by use(ThemeContext)!!
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light

    var (coverage, setCoverage) = props.dataLoader.coverage.toState()

    Stack.create() {
        Accordion {
            AccordionSummary {
                id = ElementId("panel1a-header")
                expandIcon = ExpandMore.create()

                Typography {
                    +"Warfare did play a crucial role in the territorial expansion"
                }
            }
            AccordionDetails {
                Typography {
                    Button {
                        +"Show me!"
                        onClick = {
                            val threeDPitch = props.dataLoader.threeDPitch
                            val threeDZoom = props.dataLoader.threeDZoom
                            val threeDLatitude = props.dataLoader.threeDLatitude
                            val threeDLongitude = props.dataLoader.threeDLongitude

                            val timeline = timeline {
                                val duration: Long = 20000
                                // Istanbul 41.013611, 28.955
                                parallel {
                                    property(threeDZoom, 8.0, duration)
                                    property(threeDLatitude, 41.013611, duration)
                                    property(threeDLongitude, 28.955, duration)
                                    property(threeDPitch, 60.0, duration, Easing.QUAD_IN)
                                }

                                parallel {
                                    property(threeDZoom, 5.0, duration)
                                    property(threeDLatitude, (41.013611 + 48.2) / 2, duration)
                                    property(threeDLongitude, (28.955 + 16.366667) / 2, duration)
                                }

                                // Vienna 48.2, 16.366667
                                parallel {
                                    property(threeDZoom, 8.0, duration)
                                    property(threeDLatitude, 48.2, duration)
                                    property(threeDLongitude, 16.366667, duration)
                                }

                                parallel {
                                    property(threeDZoom, 5.0, duration)
                                    property(threeDLatitude, (51.507222 + 48.2) / 2, duration)
                                    property(threeDLongitude, (-0.1275 + 16.366667) / 2, duration)
                                }

                                // London 51.507222, -0.1275
                                parallel {
                                    property(threeDZoom, 8.0, duration)
                                    property(threeDLatitude, 51.507222, duration)
                                    property(threeDLongitude, -0.1275, duration)
                                }

                                // Zurich
                                parallel {
                                    property(threeDZoom, 8.0, duration)
                                    property(threeDLatitude, 47.3769, duration)
                                    property(threeDLongitude, 8.5417, duration)
                                    property(threeDPitch, 60.0, duration, Easing.QUAD_IN)
                                }


                                parallel {
                                    property(threeDZoom, 5.0, duration)
                                    property(threeDLatitude, (47.3769 + 52.52) / 2, duration)
                                    property(threeDLongitude, (8.5417 + 13.405) / 2, duration)
                                }

                                // Berlin 52.52, 13.405
                                parallel {
                                    property(threeDZoom, 8.0, duration)
                                    property(threeDLatitude, 52.52, duration)
                                    property(threeDLongitude, 13.405, duration)
                                }
                            }

                            Playback(timeline).timeline.start()
                        }
                    }

                    +"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit amet blandit leo lobortis eget."
                }

                Link {
                    href = "https://icr.ethz.ch/publications/cshapes-2/"
                    +"Mapping The International System, 1886-2017: The CShapes 2.0 Dataset Schvitz, Guy, Seraina Rüegger, Luc Girardin, Lars-Erik Cederman, Nils Weidmann, and Kristian Skrede Gleditsch. Journal of Conflict Resolution 66(1): 144–61."
                }
            }
        }

        Accordion {
            AccordionSummary {
                id = ElementId("panel2a-header")
                expandIcon = ExpandMore.create()

                Typography {
                    +"After having increased for centuries, territorial state sizes started declining toward the end of the 19th century and have continued to do so until today"
                }
            }
            AccordionDetails {
                Typography {
                    Button {
                        +"Show me!"
                        onClick = {
                            props.dataLoader.borders.value = false
                            props.dataLoader.ethnicity.value = true
                            props.dataLoader.depthTest.value = false
                            props.dataLoader.shiftValue.value = 1790
                        }
                    }
                    +"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit amet blandit leo lobortis eget."
                }
            }
        }

        Accordion {
            AccordionSummary {
                id = ElementId("panel3a-header")
                expandIcon = ExpandMore.create()

                Typography {
                    +"States’ internal ethnic fragmentation leads to reductions of states’ size"
                }
            }
            AccordionDetails {
                Typography {
                    Button {
                        +"Show me!"
                        onClick = {
                            props.dataLoader.borders.value = true
                            props.dataLoader.ethnicity.value = false
                            props.dataLoader.depthTest.value = false
                        }
                    }
                    +"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit amet blandit leo lobortis eget."
                }
            }
        }

        Accordion {
            AccordionSummary {
                id = ElementId("panel4a-header")
                expandIcon = ExpandMore.create()

                Typography {
                    +"Cross-border presence of state-carrying ethnic groups makes state expansion more likely"
                }
            }
            AccordionDetails {
                Typography {
                    Button {
                        +"Show me!"
                        onClick = {
                            props.dataLoader.population.value = true
                            props.dataLoader.depthTest.value = true
                        }
                    }
                    +"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit amet blandit leo lobortis eget."
                }
                FormControlLabel {
                    sx {
                        width = 100.pct
                    }

                    control = Slider.create() {
                        value = coverage
                        onChange = { _, newValue, _ -> setCoverage(newValue.first().toDouble()) }
                        min = 0.0
                        max = 1.0
                        step = 0.1
                    }
                    label = ReactNode("Coverage")
                }
            }
        }

        Accordion {
            AccordionSummary {
                id = ElementId("panel5a-header")
                expandIcon = ExpandMore.create()

                Typography {
                    +"Golden ages"
                }
            }
            AccordionDetails {
                Typography {
                    +"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit amet blandit leo lobortis eget."
                }
            }
        }

        Accordion {
            AccordionSummary {
                id = ElementId("panel6a-header")
                expandIcon = ExpandMore.create()

                Typography {
                    +"Loss of power and/or unity increase the risk of civil and interstate conflict"
                }
            }
            AccordionDetails {
                Typography {
                    +"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse malesuada lacus ex, sit amet blandit leo lobortis eget."
                }
            }
        }
    }

//                                ToggleButtonGroup.create {
//                                    orientation = Orientation.vertical
//                                    size = Size.small
//
//                                    ToggleButton {
//                                        selected = true
//                                        +"Warfare did play a crucial role in the territorial expansion"
//                                    }
//                                    ToggleButton {
//                                        +"After having increased for centuries, territorial state sizes started declining toward the end of the 19th century and have continued to do so until today"
//                                    }
//                                    ToggleButton {
//                                        +"States’ internal ethnic fragmentation leads to reductions of states’ size"
//                                    }
//                                    ToggleButton {
//                                        +"Cross-border presence of state-carrying ethnic groups makes state expansion more likely"
//                                    }
//                                    ToggleButton {
//                                        +"Golden ages"
//                                    }
//                                    ToggleButton {
//                                        +"Loss of power and/or unity increase the risk of civil and interstate conflict "
//                                    }
//                                }

}